import React, { useLayoutEffect, useRef } from 'react';

import { type Culture, getPrincipal } from '@p2l/config';

import { CenterContentContainer, LanguagePicker, isHexDark, useTheme } from '@p2l/ui-public';
import { useTranslations } from '@p2l/react-utils';
import { redirectViaDlAspx } from '@p2l/utils';

import translations from './translations.json';
import type { IFooterProps } from './app.props';
import {
  SiteFooter,
  CustomFooter,
  CustomFooterWrapper,
  FooterRightContentWrapper,
  StyledFooterButton,
  LanguagePickerWrapper,
  StyledCenterContentContainer
} from './app.styles';

export const Footer = (props: IFooterProps) => {
  const { translate, onChangeCulture, culture } = useTranslations(translations);
  const theme = useTheme();

  const element = useRef<HTMLDivElement>(null);
  const principal = getPrincipal();
  const availableLanguages: Culture[] = principal?.company?.languages || [];
  const pdfLink = redirectViaDlAspx('/KursusKatalog.aspx');

  // Keep --page-footer-height in sync with the rendered footer. A ResizeObserver
  // catches every size change (content swaps on catalog change, language, viewport),
  // not just window resizes. Reset on unmount so routes without a footer don't keep
  // reserving the old height.
  useLayoutEffect(() => {
    const footer = element.current;
    if (!footer) {
      return;
    }

    const setHeight = () => {
      document.documentElement.style.setProperty('--page-footer-height', footer.offsetHeight + 'px');
    };

    setHeight();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(setHeight) : undefined;
    observer?.observe(footer);

    return () => {
      observer?.disconnect();
      document.documentElement.style.setProperty('--page-footer-height', '0px');
    };
  }, []);

  const onLanguageClick = (value: Culture) => {
    onChangeCulture(value);
    props.shouldReloadOnLanguageChange && window.location.reload();
  };

  const onPdfCatalogClick = () => {
    window.open(pdfLink, '_blank');
  };

  return (
    <SiteFooter ref={element} data-p2l-footer>
      {props.customFooterHTML ? (
        <CustomFooterWrapper>
          <CenterContentContainer>
            <CustomFooter dangerouslySetInnerHTML={{ __html: props.customFooterHTML }} />
          </CenterContentContainer>
        </CustomFooterWrapper>
      ) : null}
      <StyledCenterContentContainer>
        <FooterRightContentWrapper>
          {props.showPdfCatalog && pdfLink.length > 0 ? (
            <StyledFooterButton
              tabIndex={0}
              onClick={onPdfCatalogClick}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  onPdfCatalogClick();
                }
              }}
              role="button"
            >
              {translate('PDF_CATALOG')}
            </StyledFooterButton>
          ) : null}
          {props.feedback ? (
            <StyledFooterButton
              tabIndex={0}
              onClick={props.feedback.onClick}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  props.feedback?.onClick();
                }
              }}
              role="button"
            >
              {props.feedback.name}
            </StyledFooterButton>
          ) : null}
          {props.dataPolicy ? (
            <StyledFooterButton
              tabIndex={0}
              onClick={props.dataPolicy.onClick}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  props.dataPolicy?.onClick();
                }
              }}
              role="button"
            >
              {props.dataPolicy.name}
            </StyledFooterButton>
          ) : null}
          <LanguagePickerWrapper>
            <LanguagePicker
              popperPlacement="top"
              languages={availableLanguages}
              selectedLanguage={culture}
              onLanguageChange={onLanguageClick}
              inverted={!isHexDark(theme.components.footer.backgroundColor || '#000000')}
            />
          </LanguagePickerWrapper>
        </FooterRightContentWrapper>
      </StyledCenterContentContainer>
    </SiteFooter>
  );
};
